import { breakpoints, colors, media } from "assets/style/Variable";
import { ArrowBtnLink } from "components/effect/ArrowBtnLink";
import { GraphProgress } from "components/effect/GraphProgress";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import sunImg from "assets/images/main/weather/main-sun-img.png";
import temperatureImg from "assets/images/main/weather/main-temperature-img.png";
import { DEFAULT_ADDRESS_TEXT, dateChange, weatherClock } from "utils/common";
import { firebaseWeatherOpt, firebaseWeatherUpdate, getWeatherdepthCollectionDoc } from "utils/firebase/weather";
import { createWeatherStateFromDays, findCategory, getDisplayTemperatureRange, getStableCurrentWeatherTimeLists, getWeather, getWeatherCycleTimes, getWeatherLocalCache, pickWeatherDaysFromCache, weatherInit, weatherMerge } from "utils/weather/weather";
import { KORLocationType } from "types/weatherType";
import { getDefaultKoreanLocation, getLocationCoords } from "utils/weather/korLocation";

export const WeatherCont = () => {
  const defaultLocation = getDefaultKoreanLocation() as KORLocationType | null;
  const defaultLocationText = DEFAULT_ADDRESS_TEXT;
  const { data: weatherState } = useQuery({
    queryKey: ['mainWeatherPreview', defaultLocation?.districtCode, dateChange('year')],
    queryFn: async () => {
      if (!defaultLocation) return null;

      const localCache = getWeatherLocalCache(defaultLocation.districtCode);
      if (localCache?.res?.length) {
        return localCache;
      }

      const cacheDoc = await getWeatherdepthCollectionDoc({
        ...firebaseWeatherOpt(defaultLocation),
        col3:'year',
        doc3:`${dateChange('year')}`,
      });
      const weatherDays = pickWeatherDaysFromCache(cacheDoc, getWeatherCycleTimes().threeDays);
      if (weatherDays.length) {
        return createWeatherStateFromDays(weatherDays, defaultLocation);
      }

      const defaultCoords = getLocationCoords(defaultLocation);
      const initWeather = await weatherInit(defaultCoords);
      if (!initWeather || !initWeather.res?.length) return null;

      const ultraForecast = await getWeather(defaultCoords, 'getUltraSrtFcst');
      const mergedForecast = ultraForecast?.res?.length ? weatherMerge(initWeather, ultraForecast) : initWeather;
      const ultraNow = await getWeather(defaultCoords, 'getUltraSrtNcst');
      const mergedWeather = ultraNow?.res?.length ? weatherMerge(mergedForecast, ultraNow) : mergedForecast;

      void firebaseWeatherUpdate(defaultLocation, mergedWeather);
      return mergedWeather;
    },
    staleTime: 1000 * 60 * 10,
  });

  const todayWeather = weatherState?.res?.[0];
  const currentTime = todayWeather ? getStableCurrentWeatherTimeLists(todayWeather.timeLists, weatherClock()) : null;
  const temperatureRange = getDisplayTemperatureRange({
    forecastMin: todayWeather?.TMN,
    forecastMax: todayWeather?.TMX,
    currentTemp: findCategory(currentTime?.categoryList ?? [], 'T1H') ?? findCategory(currentTime?.categoryList ?? [], 'TMP'),
    timeLists: todayWeather?.timeLists,
  });

  return (
    <StyleWeatherCont>
      <div className="weather-inner">
        <div className="info-wrap">
          <span className="temperature-img icon"><img src={temperatureImg} alt=""></img></span>
          <span className="sun-img icon"><img src={sunImg} alt=""></img></span>
          <h2 className="title">지역별 날씨와 온도</h2>
          <p className="desc">
            오늘부터 최대 모레까지 <br />
            시간별 날씨 상태와 온도를 확인해보세요! 😄
          </p>
          <div className="btn-article">
            <ArrowBtnLink 
              link={'/weather'} 
              title={'날씨 보러 가기'}
              onColor={colors.mSlateBlue} />
          </div>
        </div>
        <div className="circles-wrap">
          <p className="location-title">{defaultLocationText} 기준</p>
          <p className="temperature-info">
            <span className="desc">최저 : <span className="min">{temperatureRange.displayMin ?? '-'} °</span></span>
            <span className="desc">최고 : <span className="max">{temperatureRange.displayMax ?? '-'} °</span></span>
          </p>
          <div className="circles-box">
            <GraphProgress 
              start={0}
              end={100}
              goal={temperatureRange.displayPercent}
              isTitle={false} />
            <div className="temperature-status">
              <strong>{temperatureRange.current ?? '-'}</strong>
              <span>°C</span>
            </div>
          </div>
        </div>
      </div>
    </StyleWeatherCont>
  )
}

const StyleWeatherCont = styled.div`
  display:flex;
  align-items:center;
  position:relative;
  min-height:100svh;
  padding:40px 0 0;
  text-align:center;
  .weather-inner {
    width:100%;
    max-width:${breakpoints.pc}px;
    margin:0 auto;
    padding:0 30px;
  }
  .info-wrap{
    display:inline-block;
    position:relative;
    .icon{
      display:inline-block;
      pointer-events:none;
      -webkit-user-drag: none;
      user-select: none;
    }
    .temperature-img{
      position:absolute;
      top:80px;
      right:-30px;
      width:auto;
      height:50px;
      transform:rotate(15deg);
      img{
        width:auto;
        height:100%;
      }
    }
    .sun-img{
      width:50px;
      margin:20px 0;
    }
  }
  .circles-wrap{
    margin-top:30px;
    .location-title {
      margin-bottom:10px;
      font-size:13px;
      color:${colors.subTextColor};
    }
    .temperature-info {
      display:flex;
      justify-content:center;
      gap:10px;
      .desc {
        font-size:14px;
        & > span{
          font-size:16px;
        }
      }
      .min{
        color:${colors.blue};
      }
      .max {
        color:${colors.red};
      }
    }
    .circles-box{
      display:inline-block;
      position:relative;
      width:300px;
      height:300px;
      margin-top:20px;
      padding:30px;
      border-radius:50%;
      box-shadow: 
        ${({theme}) => theme.type === 'dark' 
        ?`
           rgba(255, 255, 255, .25) 15px 10px 25px -20px inset, rgba(0, 0, 0, .2) -20px -15px 25px -20px inset, rgba(0, 0, 0, .3) -5px -10px 15px, rgba(0, 0, 0, .3) -10px -5px 15px, rgba(255, 255, 255, .05) 8px 10px 15px, rgba(255, 255, 255, .07) 7px 10px 23px;
        `
        : `
           rgba(0, 0, 0, 0.25) 15px 10px 25px -20px inset, rgba(255, 255, 255, 0.2) -20px -15px 25px -20px inset, rgba(255, 255, 255, .3) -5px -10px 15px, rgba(255, 255, 255, .3) -10px -5px 15px, rgba(0, 0, 0, .05) 8px 10px 15px, rgba(0, 0, 0, .07) 7px 10px 23px;
        `};
    }
    .temperature-status {
      position:absolute;
      top:50%;
      left:50%;
      transform:translate(-50%, -50%);
      strong {
        font-size:54px;
        line-height:1;
      }
      span {
        margin-left:4px;
        font-size:22px;
        vertical-align:top;
      }
    }
  }
  ${media.mo}{
    
  }
`;
